import { APP_PATHS } from '@/config/routes/app-paths';

export const APP_NAME = 'Wholesaler Activation Service Platform (WASP)';

export const SETUP_SIDEBAR_PANEL_WIDTH = '290px';

/**
 * Caps the scrollable content column of every dashboard route so ultra-wide and 4K displays centre
 * the page instead of stretching tables and cards across the full viewport. Set to the width of a
 * standard 16:9 monitor (1920x1080), so the layout is unchanged on every normal screen and only a
 * larger-than-1080p display (a 4K panel at 3840x2160, an ultra-wide) gets gutters.
 */
export const DASHBOARD_CONTENT_MAX_WIDTH = '120rem';

/**
 * Every form action button — Save, Cancel, Add, and their siblings in a form footer or a tab
 * header — renders at this size. `PrimaryButton` defaults to `lg` and a bare `Button` defaults to
 * `md`, so a Cancel/Save pair that leaves both unset renders mismatched. Pass this to both.
 */
export const FORM_ACTION_BUTTON_SIZE = 'sm';

/**
 * Floor width for every `SaveButton`, so a row of them lines up and the spinner that replaces the
 * label mid-save never reflows the toolbar around it.
 */
export const SAVE_BUTTON_MIN_WIDTH = '9rem';

export const API_ROUTES = {
  auth: {
    login: '/auth/login',
    logout: '/auth/logout',
    profile: '/auth/profile',
    refresh: '/auth/refresh',
    mockUsers: '/auth/mock-users',
    // Sign-in is a single-factor emailed one-time credential — there is no password and no
    // authenticator. `signInRequest` mails a link and a 6-digit code and always answers 200 even
    // for an address with no account (a truthful 404 would turn it into a customer list), so the
    // UI can never say "no account with that email". `signInRedeem` is the only call that spends
    // the credential, and it takes either { token } or { email, code } — never both. The emailed
    // link redeems on arrival, so `signInValidateToken` (a non-consuming peek) is unused by the app
    // and kept only because the backend still exposes it.
    signInRequest: '/auth/signin/request',
    signInValidateToken: '/auth/signin/validate-token',
    signInRedeem: '/auth/signin/redeem',
    // DEFERRED, not removed — authenticator sign-in is postponed, not dropped. Verified live on
    // 2026-08-19: POST /auth/totp-token still answers (400 naming Email + Code as required), and
    // the undocumented POST /auth/signin requires Email + TotpCode. Uncomment when MFA returns.
    // totpToken: '/auth/totp-token',
    me: '/me',
    meWorkspaces: '/me/workspaces',
  },
  activation: {
    resend: '/auth/activate/resend',
    // Activation is now the same one-time email credential as sign-in: peek the link's token with
    // `validateToken` (non-consuming), then spend it with `redeem`. Redeeming marks the admin
    // Verified *and* returns a session, so activation ends signed in rather than handing the user
    // back to the sign-in page. The TOTP enrollment pair (`/activate/password`, `/activate/mfa`)
    // and the token-bound `/activate/confirm-email` step are deferred, not retired — see
    // docs/passwordless-email-otp-handoff.md and the commented block below.
    validateToken: '/auth/activate/validate-token',
    redeem: '/auth/activate/redeem',
    // DEFERRED, not removed — the TOTP enrollment pair and the token-bound emailed-code step are
    // switched off for the passwordless flow, not deleted. All three are still live on the backend
    // (present in swagger, verified 2026-08-19). Uncomment when MFA returns.
    // confirmEmail: '/auth/activate/confirm-email',
    // begin: '/auth/activate/password',
    // mfa: '/auth/activate/mfa',
  },
  register: {
    create: '/auth/register',
    checkEmail: '/auth/register/check-email',
  },
  setup: {
    // Real backend endpoint, tagged "Signup" in swagger — the self-service path for
    // this wizard. POST /wholesalers (tagged "Catalog", alongside GET /admins and
    // GET /wholesalers listing every tenant) is platform-admin only and 403s for a
    // regular wholesaler admin. Response has no documented schema (200 OK) — field
    // names inferred from the poll endpoints' path params below. See
    // features/wholesaler-setup/api/setup.ts for the request body shape.
    activate: '/signup/activate',
  },
  billing: {
    balance: '/billing/balance',
    paymentMethod: '/billing/payment-method',
    paymentMethods: '/billing/payment-methods',
    setDefaultPaymentMethod: (id: string) => `/billing/payment-methods/${id}/default`,
    deletePaymentMethod: (id: string) => `/billing/payment-methods/${id}`,
    invoiceRecords: '/billing/invoice-records',
    monthlySpend: '/billing/monthly-spend',
    setupIntent: '/billing/setup-intent',
    chargePreview: '/billing/charge-preview',
    creditThresholds: '/billing/credit-thresholds',
    receipt: (receiptId: string) => `/billing/receipts/${receiptId}`,
    wholesalerCredit: (wholesalerId: string) => `/billing/wholesalers/${wholesalerId}/credit`,
  },
  // Platform-wide credit rules (free credit, currency, top-up floor and ceiling) — the
  // read side of what a platform admin sets in Admin Settings > Pricing Admin. Gated on
  // `billing.view` rather than a platform-admin role, so unlike /admin/pricing-config it
  // is readable from the billing feature without breaking the no-admin-reads rule.
  pricing: {
    settings: '/pricing/settings',
  },
  overview: '/overview',
  // Platform country reference data — id, name, code, and the carriage flags a platform
  // admin sets in Admin Settings > Country Admin (`byoc`, `pingCoCts`). Every role's token
  // carries `wholesalers.view`, so this is the non-admin read of what /admin/country-config
  // writes; unlike /geography/countries it is uncached server-side, so an admin's save shows
  // up on the next fetch.
  countries: '/countries',
  wholesalers: {
    current: '/wholesalers/current',
    kyc: '/wholesalers/current/kyc',
    // Creates a Stripe Identity VerificationSession; client passes clientSecret to
    // stripe.verifyIdentity(), then calls POST kyc to record the submission. The real
    // verification result (kycStatus: Unapproved/Submitted/Rejected/Approved) arrives
    // via a backend-side Stripe webhook, not synchronously from either endpoint.
    kycSession: '/wholesalers/current/kyc/session',
    // Multipart image upload (field name "file"); backend caps it at 5 MB and accepts
    // PNG/JPG/SVG/WebP. Responds 200 with no documented body, so the stored URL has to be
    // read back from GET /wholesalers/current rather than taken from the response.
    logo: (wholesalerId: string) => `/wholesalers/${wholesalerId}/logo`,
  },
  services: {
    config: (wholesalerId: string) => `/wholesalers/${wholesalerId}/config`,
    catalog: '/services/catalog',
  },
  // Per-offering catalog writes. `PUT /services/catalog` is upsert-only server-side — it
  // never removes anything — so dropping an item from that payload is silently a no-op and
  // a delete has to go through these instead. Both hard-delete platform-wide.
  catalog: {
    service: (id: string) => `/catalog/services/${id}`,
    feature: (id: string) => `/catalog/features/${id}`,
  },
  team: {
    members: (wholesalerId: string) => `/wholesalers/${wholesalerId}/team`,
    invite: (wholesalerId: string) => `/wholesalers/${wholesalerId}/team/invite`,
    updateRoles: (wholesalerId: string) => `/wholesalers/${wholesalerId}/team/roles`,
    updateMember: (wholesalerId: string, userId: string) =>
      `/wholesalers/${wholesalerId}/team/${userId}`,
  },
  notifications: {
    list: (userId: string) => `/users/${userId}/notifications`,
    updateStatus: (userId: string, notificationId: string) =>
      `/users/${userId}/notifications/${notificationId}/status`,
    clearAll: (userId: string) => `/users/${userId}/notifications/clear-all`,
  },
  feedback: {
    submit: '/feedback',
  },
  admin: {
    countryConfig: '/admin/country-config',
    servicesConfig: '/admin/services-config',
    pricingConfig: '/admin/pricing-config',
    ctsFormConfig: '/admin/cts-form-config',
    ctsFormCountry: (countryCode: string) => `/admin/cts-form-config/${countryCode}`,
    apiKeys: '/admin/api-keys',
    apiKey: (id: string) => `/admin/api-keys/${id}`,
  },
  roles: {
    list: '/roles',
    detail: (id: string) => `/roles/${id}`,
    permissions: (id: string) => `/roles/${id}/permissions`,
  },
  permissions: {
    list: '/permissions',
    detail: (id: string) => `/permissions/${id}`,
  },
};

export const QUERY_KEYS = {
  auth: {
    profile: ['auth', 'profile'] as const,
    mockUsers: ['auth', 'mock-users'] as const,
    user: (userId: string) => ['auth', 'user', userId] as const,
  },
  pricing: {
    settings: ['pricing', 'settings'] as const,
  },
  billing: {
    balance: (wholesalerScope: string) => ['billing', 'balance', wholesalerScope] as const,
    paymentMethod: (wholesalerScope: string) =>
      ['billing', 'payment-method', wholesalerScope] as const,
    paymentMethods: (wholesalerScope: string) =>
      ['billing', 'payment-methods', wholesalerScope] as const,
    chargePreview: (amountUsd: number, paymentMethodId: string) =>
      ['billing', 'charge-preview', amountUsd, paymentMethodId] as const,
    creditThresholds: (wholesalerScope: string) =>
      ['billing', 'credit-thresholds', wholesalerScope] as const,
    invoiceRecords: (wholesalerId: string) => ['billing', 'invoice-records', wholesalerId] as const,
    monthlySpend: (wholesalerId: string) => ['billing', 'monthly-spend', wholesalerId] as const,
    receipt: (receiptId: string) => ['billing', 'receipt', receiptId] as const,
    wholesalerCredit: (wholesalerId: string) =>
      ['billing', 'wholesaler-credit', wholesalerId] as const,
  },
  overview: {
    byWholesaler: (wholesalerId: string) => ['overview', 'wholesaler', wholesalerId] as const,
  },
  countries: ['countries'] as const,
  wholesalers: {
    current: (wholesalerId: string) => ['wholesalers', 'current', wholesalerId] as const,
  },
  services: {
    config: (wholesalerId: string) => ['services', 'config', wholesalerId] as const,
    catalogPrefix: ['services', 'catalog'] as const,
    catalog: (wholesalerId: string) => ['services', 'catalog', wholesalerId] as const,
  },
  team: {
    members: (wholesalerId: string) => ['team', 'members', wholesalerId] as const,
  },
  notifications: {
    list: (userId: string) => ['notifications', 'list', userId] as const,
  },
  admin: {
    countryConfig: ['admin', 'country-config'] as const,
    servicesConfig: ['admin', 'services-config'] as const,
    pricingConfig: ['admin', 'pricing-config'] as const,
    ctsFormConfig: ['admin', 'cts-form-config'] as const,
    apiKeys: ['admin', 'api-keys'] as const,
  },
  roles: {
    list: ['roles', 'list'] as const,
  },
  permissions: {
    list: ['permissions', 'list'] as const,
  },
} as const;

/**
 * Length of the one-time code emailed by both the activation and sign-in flows. One constant
 * because it is one credential issued by one backend service — the separate TOTP length went with
 * the authenticator.
 */
export const EMAIL_CODE_LENGTH = 6;

// DEFERRED, not removed — TOTP codes are RFC 6238's six digits, which is why this stayed a
// separate constant from the emailed code above. Uncomment when MFA returns.
// export const MFA_CODE_LENGTH = 6;

/**
 * Client-side gap between one-time-code resends. This throttles the UI only — a refresh clears it
 * — so it is a courtesy against double-clicks, not the real limit. The backend enforces its own
 * issuance throttle on `/auth/signin/request` (3 per 15 minutes per address, answered with a 429);
 * `/auth/activate/resend` is still unthrottled.
 */
export const RESEND_COOLDOWN_SECONDS = 30;

export const CACHE_TIME = {
  stale: 1000 * 60 * 5,
  gc: 1000 * 60 * 10,
} as const;

export const SESSION_TIMEOUT_MS = 3 * 60 * 60 * 1000; // 3 hours

/**
 * How long an amount must sit unchanged before a "below the minimum" message appears. Every
 * amount is below the minimum while it is being typed — $2 on the way to $20 — so this message
 * waits for a pause. Amounts over the maximum are not delayed: more keystrokes only worsen them.
 */
export const AMOUNT_BELOW_MIN_DELAY_MS = 800;

export const ROUTES = {
  home: APP_PATHS.home,
  overview: APP_PATHS.overview,
  setup: APP_PATHS.setup,
} as const;
